import L from "leaflet";

export const FEET_TO_METERS = 0.3048;

// Closest point on the drawn route. "along" is meters from the route start.
export function projectOntoRoute(lat, lng, routePts) {
  if (!routePts || routePts.length < 2) return null;
  const p = L.latLng(lat, lng);
  let total = 0;
  let best = null;
  for (let i = 0; i < routePts.length - 1; i++) {
    const a = routePts[i];
    const b = routePts[i + 1];
    const segLen = a.distanceTo(b);
    const dx = b.lat - a.lat;
    const dy = b.lng - a.lng;
    const len2 = dx * dx + dy * dy;
    const t =
      len2 > 0
        ? Math.max(0, Math.min(1, ((p.lat - a.lat) * dx + (p.lng - a.lng) * dy) / len2))
        : 0;
    const proj = L.latLng(a.lat + t * dx, a.lng + t * dy);
    const perp = p.distanceTo(proj);
    if (!best || perp < best.perp) {
      best = { along: total + t * segLen, perp, lat: proj.lat, lng: proj.lng };
    }
    total += segLen;
  }
  return best;
}

export function pointAtAlong(routePts, along) {
  let remaining = along;
  for (let i = 0; i < routePts.length - 1; i++) {
    const a = routePts[i];
    const b = routePts[i + 1];
    const segLen = a.distanceTo(b);
    if (remaining <= segLen || i === routePts.length - 2) {
      const t = segLen > 0 ? Math.max(0, Math.min(1, remaining / segLen)) : 0;
      return { lat: a.lat + t * (b.lat - a.lat), lng: a.lng + t * (b.lng - a.lng) };
    }
    remaining -= segLen;
  }
  return { lat: routePts[0].lat, lng: routePts[0].lng };
}

// "145 Lee Ave" for one house, "145–171 Lee Ave" for a range on the main street.
export function defaultStopName(items) {
  const byStreet = {};
  items.forEach((it) => {
    const street = (it.street || "").trim();
    if (!street) return;
    const key = street.toLowerCase();
    if (!byStreet[key]) byStreet[key] = { street, houses: [] };
    byStreet[key].houses.push((it.house || "").trim());
  });
  const top = Object.values(byStreet).sort((a, b) => b.houses.length - a.houses.length)[0];
  if (!top) return "";
  const nums = top.houses
    .map((h) => parseInt(h, 10))
    .filter((n) => !isNaN(n))
    .sort((a, b) => a - b);
  if (!nums.length) return top.street;
  if (nums[0] === nums[nums.length - 1]) return nums[0] + " " + top.street;
  return nums[0] + "–" + nums[nums.length - 1] + " " + top.street;
}

// Groups students so nobody walks more than maxWalkFeet along the street to their
// stop (the stop sits mid-way, so one stop covers a stretch of 2 × maxWalkFeet).
// Stretches that are too long are split at their widest gap between neighbours,
// then adjacent groups that still fit together are merged back.
// items: [{ studentID, lat, lng, house, street }]
export function groupAlongRoute(items, routePts, maxWalkFeet) {
  const maxSpan = 2 * Math.max(1, maxWalkFeet || 100) * FEET_TO_METERS;
  const projected = items
    .map((it) => ({ it, along: (projectOntoRoute(it.lat, it.lng, routePts) || {}).along }))
    .filter((x) => x.along != null)
    .sort((a, b) => a.along - b.along);
  if (!projected.length) return [];

  const span = (g) => g[g.length - 1].along - g[0].along;
  const pending = [projected];
  const split = [];
  while (pending.length) {
    const g = pending.shift();
    if (g.length < 2 || span(g) <= maxSpan) {
      split.push(g);
      continue;
    }
    let cut = 1;
    for (let i = 2; i < g.length; i++) {
      if (g[i].along - g[i - 1].along > g[cut].along - g[cut - 1].along) cut = i;
    }
    pending.unshift(g.slice(0, cut), g.slice(cut));
  }
  split.sort((a, b) => a[0].along - b[0].along);

  const groups = [];
  split.forEach((g) => {
    const last = groups[groups.length - 1];
    if (last && g[g.length - 1].along - last[0].along <= maxSpan) {
      groups[groups.length - 1] = last.concat(g);
    } else {
      groups.push(g);
    }
  });

  return groups.map((g) => {
    const along = (g[0].along + g[g.length - 1].along) / 2;
    const pt = pointAtAlong(routePts, along);
    return {
      lat: pt.lat,
      lng: pt.lng,
      along,
      name: defaultStopName(g.map((m) => m.it)),
      studentIDs: g.map((m) => m.it.studentID),
    };
  });
}

// Renumbers 1..n by position along the route; stops that can't be projected keep
// their relative order at the end.
export function numberStopsAlongRoute(stops, routePts) {
  const keyed = stops.map((s, i) => {
    const proj = s.lat != null && s.lng != null ? projectOntoRoute(s.lat, s.lng, routePts) : null;
    return { s, along: proj ? proj.along : Infinity, i };
  });
  keyed.sort((a, b) => a.along - b.along || a.i - b.i);
  keyed.forEach((k, idx) => {
    k.s.stopNumber = idx + 1;
  });
  return keyed.map((k) => k.s);
}

// Inserts a new stop by its route position without disturbing the order the user
// already set for the other stops.
export function insertStopInOrder(stops, newStop, routePts) {
  const ordered = stops.slice().sort((a, b) => a.stopNumber - b.stopNumber);
  const newProj = projectOntoRoute(newStop.lat, newStop.lng, routePts);
  let index = ordered.length;
  if (newProj) {
    const next = ordered.findIndex((s) => {
      const p = s.lat != null ? projectOntoRoute(s.lat, s.lng, routePts) : null;
      return p && p.along > newProj.along;
    });
    if (next !== -1) index = next;
  }
  ordered.splice(index, 0, newStop);
  ordered.forEach((s, i) => {
    s.stopNumber = i + 1;
  });
  return ordered;
}

export function stopIcon(number, color, highlighted) {
  const size = highlighted ? 30 : 24;
  const html =
    '<div style="width:' + size + "px;height:" + size + "px;border-radius:6px;" +
    "background:" + color + ";color:#fff;border:2px solid #fff;" +
    "box-shadow:0 1px 4px rgba(0,0,0,.45);display:flex;align-items:center;" +
    "justify-content:center;font:700 " + (highlighted ? 13 : 12) + "px/1 Roboto,Arial,sans-serif;" +
    '">' + number + "</div>";
  return L.divIcon({
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: "",
  });
}
